import { useParams, Link, useNavigate } from 'react-router-dom';
import { usePage, useIsFavorited, useAddFavorite, useRemoveFavorite, useIsSubscribed, useSubscribe, useUnsubscribe, useCopyPage, useInlineComments, useCreateComment, useDeleteComment, useResolveComment } from '@/hooks';
import { Card, CardContent, Button, Skeleton, CodeBlock, Embed, isEmbeddableUrl, InlineCommentPanel, useTextSelection, SelectionTooltip, CommentInputModal } from '@/components/ui';
import { formatRelativeTime, formatDate } from '@/lib/utils';
import { Toc } from '@/components/Toc';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeHighlight from 'rehype-highlight';
import rehypeRaw from 'rehype-raw';
import DOMPurify from 'dompurify';
import { remarkWikiLink } from '@/lib/remark-wiki-link';
import { ArrowLeft, Edit, History, Eye, Lock, Users, Calendar, Download, Heart, Bell, Globe, Copy, MessageSquare, Share2 } from 'lucide-react';
import { useAuthStore } from '@/stores';
import api from '@/lib/api';
import toast from 'react-hot-toast';
import 'highlight.js/styles/github.css';
import { useRef, useState, useCallback } from 'react';
import { ShareDialog } from '@/components/ShareDialog';

export function PageDetail() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: page, isLoading, error } = usePage(slug!);
  const { user, isAuthenticated } = useAuthStore();
  const { data: isFavorited = false } = useIsFavorited(slug!, isAuthenticated);
  const addFavorite = useAddFavorite();
  const removeFavorite = useRemoveFavorite();
  const { data: isSubscribed = false } = useIsSubscribed(slug!, isAuthenticated);
  const subscribe = useSubscribe();
  const unsubscribe = useUnsubscribe();
  const copyPage = useCopyPage();

  // Inline comments
  const contentRef = useRef<HTMLDivElement>(null);
  const [showComments, setShowComments] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [commentModalOpen, setCommentModalOpen] = useState(false);
  const [pendingSelection, setPendingSelection] = useState<{
    text: string;
    startOffset: number;
    endOffset: number;
  } | null>(null);

  const { comments, refetch: refetchComments } = useInlineComments(slug!);
  const createComment = useCreateComment(slug!);
  const deleteComment = useDeleteComment(slug!);
  const resolveComment = useResolveComment(slug!);

  const { selection, clearSelection, handleMouseUp } = useTextSelection(contentRef, comments);

  const handleAddComment = useCallback(async (content: string) => {
    if (!pendingSelection) return;

    await createComment.mutate(
      {
        content,
        startOffset: pendingSelection.startOffset,
        endOffset: pendingSelection.endOffset,
        selectedText: pendingSelection.text,
      },
      {
        onSuccess: () => {
          toast.success('评论已添加');
          refetchComments();
          setPendingSelection(null);
        },
        onError: () => toast.error('添加评论失败'),
      }
    );
  }, [pendingSelection, createComment, refetchComments]);

  const handleReply = useCallback(async (parentId: number, content: string) => {
    await createComment.mutate(
      { content, parentId },
      {
        onSuccess: () => {
          toast.success('回复已添加');
          refetchComments();
        },
        onError: () => toast.error('添加回复失败'),
      }
    );
  }, [createComment, refetchComments]);

  const handleResolve = useCallback(async (commentId: number, resolved: boolean) => {
    await resolveComment.mutate(commentId, resolved, {
      onSuccess: () => {
        toast.success(resolved ? '评论已标记为已解决' : '评论已重新打开');
        refetchComments();
      },
      onError: () => toast.error('操作失败'),
    });
  }, [resolveComment, refetchComments]);

  const handleDelete = useCallback(async (commentId: number) => {
    await deleteComment.mutate(commentId, {
      onSuccess: () => {
        toast.success('评论已删除');
        refetchComments();
      },
      onError: () => toast.error('删除失败'),
    });
  }, [deleteComment, refetchComments]);

  const openCommentModal = useCallback(() => {
    if (selection) {
      setPendingSelection({
        text: selection.text,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
      });
      setCommentModalOpen(true);
      clearSelection();
    }
  }, [selection, clearSelection]);

  const handleExport = async () => {
    if (!slug) return;

    try {
      const response = await api.get(`/pages/${slug}/export`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], { type: 'text/markdown' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${slug}.md`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('导出成功');
    } catch (err) {
      toast.error('导出失败');
    }
  };

  const handleToggleFavorite = () => {
    if (!slug) return;

    if (isFavorited) {
      removeFavorite.mutate(slug, {
        onSuccess: () => toast.success('已取消收藏'),
        onError: () => toast.error('操作失败'),
      });
    } else {
      addFavorite.mutate(slug, {
        onSuccess: () => toast.success('已添加收藏'),
        onError: () => toast.error('操作失败'),
      });
    }
  };

  const handleToggleSubscribe = () => {
    if (!slug) return;

    if (isSubscribed) {
      unsubscribe.mutate(slug, {
        onSuccess: () => toast.success('已取消订阅'),
        onError: () => toast.error('操作失败'),
      });
    } else {
      subscribe.mutate(slug, {
        onSuccess: () => toast.success('已订阅，页面更新时会收到通知'),
        onError: () => toast.error('操作失败'),
      });
    }
  };

  const handleCopyPage = () => {
    if (!slug) return;

    copyPage.mutate(slug, {
      onSuccess: (newPage) => {
        toast.success('页面已复制');
        navigate(`/pages/${newPage.slug}/edit`);
      },
      onError: (err: any) => {
        toast.error(err.message || '复制失败');
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-1/2" />
        <Skeleton className="h-4 w-1/4" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <p className="text-muted-foreground mb-4">页面不存在或无权访问</p>
          <Link to="/pages">
            <Button variant="outline">
              <ArrowLeft className="h-4 w-4 mr-2" />
              返回列表
            </Button>
          </Link>
        </CardContent>
      </Card>
    );
  }

  const canEdit = isAuthenticated && user && (
    user.role === 'admin' || page.author_id === user.id
  );

  const getVisibilityLabel = (visibility: string) => {
    switch (visibility) {
      case 'anonymous':
        return { icon: <Globe className="h-4 w-4" />, label: '匿名公开' };
      case 'public':
        return { icon: <Eye className="h-4 w-4" />, label: '公开' };
      case 'editors':
        return { icon: <Users className="h-4 w-4" />, label: '编辑者可见' };
      case 'private':
        return { icon: <Lock className="h-4 w-4" />, label: '私有' };
      default:
        return { icon: <Eye className="h-4 w-4" />, label: '公开' };
    }
  };

  const visibilityInfo = getVisibilityLabel(page.visibility);

  // Sanitize content before rendering
  const sanitizedContent = DOMPurify.sanitize(page.content);

  return (
    <div className="flex gap-8">
      {/* Main content area */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Header */}
        <div className="space-y-4">
          {/* Top toolbar: back button + action buttons */}
          <div className="flex items-center justify-between">
            <Link to="/pages">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                返回
              </Button>
            </Link>

            <div className="flex items-center gap-2">
              {isAuthenticated && (
                <Button
                  variant={showComments ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowComments(!showComments)}
                >
                  <MessageSquare className="h-4 w-4 mr-2" />
                  评论 ({comments.length})
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleExport}>
                <Download className="h-4 w-4 mr-2" />
                导出
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShareDialogOpen(true)}>
                <Share2 className="h-4 w-4 mr-2" />
                分享
              </Button>
              {isAuthenticated && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyPage}
                  disabled={copyPage.isPending}
                >
                  <Copy className="h-4 w-4 mr-2" />
                  {copyPage.isPending ? '复制中...' : '复制'}
                </Button>
              )}
              {isAuthenticated && (
                <>
                  <Button
                    variant={isFavorited ? 'default' : 'outline'}
                    size="sm"
                    onClick={handleToggleFavorite}
                    disabled={addFavorite.isPending || removeFavorite.isPending}
                  >
                    <Heart className={`h-4 w-4 mr-2 ${isFavorited ? 'fill-current' : ''}`} />
                    {isFavorited ? '已收藏' : '收藏'}
                  </Button>
                  <Button
                    variant={isSubscribed ? 'default' : 'outline'}
                    size="sm"
                    onClick={handleToggleSubscribe}
                    disabled={subscribe.isPending || unsubscribe.isPending}
                  >
                    <Bell className={`h-4 w-4 mr-2 ${isSubscribed ? 'fill-current' : ''}`} />
                    {isSubscribed ? '已订阅' : '订阅'}
                  </Button>
                </>
              )}
              {canEdit && (
                <>
                  <Link to={`/pages/${slug}/edit`}>
                    <Button variant="outline" size="sm">
                      <Edit className="h-4 w-4 mr-2" />
                      编辑
                    </Button>
                  </Link>
                  <Link to={`/pages/${slug}/history`}>
                    <Button variant="ghost" size="sm">
                      <History className="h-4 w-4 mr-2" />
                      历史
                    </Button>
                  </Link>
                </>
              )}
            </div>
          </div>

          {/* Title */}
          <h1 className="text-3xl font-bold">{page.title}</h1>

          {/* Meta info */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <div className="flex items-center gap-1">
              {visibilityInfo.icon}
              <span>{visibilityInfo.label}</span>
            </div>
            <span>•</span>
            <span>作者: {page.author?.username}</span>
            <span>•</span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              <span>创建于 {formatDate(page.created_at)}</span>
            </span>
            <span>•</span>
            <span>更新于 {formatRelativeTime(page.updated_at)}</span>
          </div>

          {/* Tags */}
          {page.tags && page.tags.length > 0 && (
            <div className="flex items-center space-x-2">
              {page.tags.map((tag) => (
                <Link
                  key={tag.id}
                  to={`/tags/${tag.name}`}
                  className="px-3 py-1 bg-muted rounded-full text-sm hover:bg-muted/80 transition-colors"
                >
                  {tag.name}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Content */}
        <div className="prose prose-slate dark:prose-invert max-w-none relative">
          <div
            ref={contentRef}
            className="markdown-content"
            onMouseUp={isAuthenticated ? handleMouseUp : undefined}
          >
            {/* Selection tooltip */}
            {selection && isAuthenticated && (
              <SelectionTooltip
                selection={selection}
                onAddComment={openCommentModal}
                onDismiss={clearSelection}
              />
            )}
            <ReactMarkdown
              remarkPlugins={[remarkGfm, remarkWikiLink]}
              rehypePlugins={[rehypeHighlight, rehypeRaw]}
              components={{
                code: CodeBlock,
                iframe: ({ src, title, ...props }) => {
                  // Check if it's an embeddable URL
                  if (src && isEmbeddableUrl(src)) {
                    return <Embed url={src} title={title} />;
                  }
                  return (
                    <iframe
                      src={src}
                      title={title}
                      {...props}
                      className="w-full aspect-video rounded-lg border border-border my-4"
                      allowFullScreen
                    />
                  );
                },
                a: ({ href, children, className }) => {
                  // Check if it's a wiki link
                  if (href?.startsWith('/pages/') && className?.includes('wiki-link')) {
                    return (
                      <Link to={href} className="text-primary hover:underline font-medium">
                        {children}
                      </Link>
                    );
                  }
                  return (
                    <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                      {children}
                    </a>
                  );
                },
              }}
            >
              {sanitizedContent}
            </ReactMarkdown>
          </div>
        </div>
      </div>

      {/* TOC sidebar - desktop */}
      <div className="hidden lg:block shrink-0 w-64">
        <div className="sticky top-20">
          <Toc content={page.content} />
        </div>
      </div>

      {/* TOC - mobile */}
      <Toc content={page.content} className="lg:hidden" />

      {/* Inline comments panel */}
      {showComments && (
        <InlineCommentPanel
          comments={comments}
          onReply={handleReply}
          onResolve={handleResolve}
          onDelete={handleDelete}
          onClose={() => setShowComments(false)}
        />
      )}

      {/* Comment input modal */}
      <CommentInputModal
        isOpen={commentModalOpen}
        selectedText={pendingSelection?.text || ''}
        onClose={() => {
          setCommentModalOpen(false);
          setPendingSelection(null);
        }}
        onSubmit={handleAddComment}
      />

      {/* Share dialog */}
      {shareDialogOpen && (
        <ShareDialog
          isOpen={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          pageSlug={slug!}
          pageTitle={page.title}
        />
      )}
    </div>
  );
}
